import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PentairTextField } from '../../../theme/components/pentair-text-field';
import { Address, InputAddress } from '../../../types/operations';
import SelectCountry from '../../form/fields/select-country';
import { FormOptions, useFormData } from '../../form/use-formdata';
import { requiredString } from '../../form/validators';

const defaultRecord: InputAddress = {
  id: '',
  addressLine1: '',
  city: '',
  country: '',
  postal: '',
  organisationName: '',
};
const formOptions: FormOptions<InputAddress> = {
  validations: {
    addressLine1: requiredString,
    city: requiredString,
    country: requiredString,
    postal: requiredString,
    organisationName: requiredString,
  },
};

export const AddressForm: React.FC<{
  initialRecord?: Address | null;
  disabled?: boolean;
  onSubmit?: (record: InputAddress) => Promise<any>;
}> = ({ disabled, onSubmit, initialRecord = defaultRecord }) => {
  const { t } = useTranslation();

  formOptions.onSubmit = onSubmit;
  formOptions.disabled = disabled;
  const { change, record, validationErrors, submitForm } = useFormData<InputAddress>(initialRecord, formOptions);

  return (
    <Box component="form" onSubmit={submitForm}>
      <PentairTextField
        label={t('ecommerce:organisationName.label', { defaultValue: 'Organisation Name' })}
        description={t('ecommerce:organisationName.description')}
        placeholder={t('ecommerce:organisationName.placeholder', { defaultValue: 'Organisation Name' })}
        value={record?.organisationName || ''}
        onChange={(e) => change('organisationName', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['organisationName']}
        helperText={validationErrors['organisationName']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('ecommerce:addressLine1.label', { defaultValue: 'Address line 1' })}
        description={t('ecommerce:addressLine1.description')}
        placeholder={t('ecommerce:addressLine1.placeholder', { defaultValue: 'Address line 1' })}
        value={record?.addressLine1 || ''}
        onChange={(e) => change('addressLine1', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['addressLine1']}
        helperText={validationErrors['addressLine1']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('ecommerce:addressLine2.label', { defaultValue: 'Address line 2' })}
        description={t('ecommerce:addressLine2.description')}
        placeholder={t('ecommerce:addressLine2.placeholder', { defaultValue: 'Address line 2' })}
        value={record?.addressLine2 || ''}
        onChange={(e) => change('addressLine2', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['addressLine2']}
        helperText={validationErrors['addressLine2']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('ecommerce:addressLine3.label', { defaultValue: 'Address line 3' })}
        description={t('ecommerce:addressLine3.description')}
        placeholder={t('ecommerce:addressLine3.placeholder', { defaultValue: 'Address line 3' })}
        value={record?.addressLine3 || ''}
        onChange={(e) => change('addressLine3', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['addressLine3']}
        helperText={validationErrors['addressLine3']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('ecommerce:postal.label', { defaultValue: 'Postal' })}
        description={t('ecommerce:postal.description')}
        placeholder={t('ecommerce:postal.placeholder', { defaultValue: 'Postal' })}
        value={record?.postal || ''}
        onChange={(e) => change('postal', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['postal']}
        helperText={validationErrors['postal']}
        disabled={disabled}
      />

      <PentairTextField
        label={t('ecommerce:city.label', { defaultValue: 'City' })}
        description={t('ecommerce:city.description')}
        placeholder={t('ecommerce:city.placeholder', { defaultValue: 'City' })}
        value={record?.city || ''}
        onChange={(e) => change('city', e.target.value)}
        variant="standard"
        margin="dense"
        error={!!validationErrors['city']}
        helperText={validationErrors['city']}
        disabled={disabled}
      />

      <SelectCountry
        id="country"
        label={t('ecommerce:country.label', { defaultValue: 'Country' })}
        description={t('ecommerce:country.description', { defaultValue: '' })}
        // placeholder={t('ecommerce:country.placeholder', {
        //   defaultValue: 'Enter country',
        // })}
        value={record?.country || ''}
        onChange={(e, val) => change('country', val?.l)}
        margin="dense"
        error={!!validationErrors['country']}
        helperText={validationErrors['country']}
        disabled={disabled}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={disabled}
        size="small"
        sx={{ marginTop: 1, marginBottom: [6, 6, 0], float: ['right', 'right', 'none'] }}
      >
        {t('ecommerce:buttonLabel.saveAddress', { defaultValue: 'Save address' })}
      </Button>
    </Box>
  );
};
